import { createElement } from "commonAssets/lib/createElement";
import formSpec from "../docs/formDefault.json";

class LossesTableSelectConfig {
    tds = () => null;
    options = [];
    addButton = () => null;
    removeButton = () => null;
    listeners = new Map();
    selectedValues = new Map();
    limit = null;
    amountInputSelector = "";
    errorContainerId = "";
    constructor(
        options,
        tdsSelector,
        addButtonSelector,
        removeButtonSelector,
        limit = null,
        amountInputSelector = "",
        errorContainerId = ""
    ) {
        this.options = options;
        this.tds = () => document.querySelectorAll(tdsSelector);
        this.addButton = () => document.querySelector(addButtonSelector);
        this.removeButton = () => document.querySelector(removeButtonSelector);
        this.limit = limit;
        this.amountInputSelector = amountInputSelector;
        this.errorContainerId = errorContainerId;
    }
    setValue(key, value) {
        this.selectedValues.set(key, value);
        this.refreshSelectValues();
    }
    getValue(key) {
        return this.selectedValues.get(key);
    }
    deleteValue(key) {
        this.selectedValues.delete(key);
        this.refreshSelectValues();
    }
    refreshSelectValues() {
        const tds = this.tds();
        if (tds.length === 0) return;
        const availableOptions = this.options.filter(
            (o) =>
                o.value === "" ||
                !this.selectedValues.values().some((v) => v === o.value)
        );
        tds.forEach((td) => {
            const select = td.querySelector("select");
            if (!select) return;
            const prevValue = select.value;
            // Clear existing options first
            select.innerHTML = "";

            // Add all options, but hide unavailable ones
            this.options.forEach((option) => {
                const isAvailable = availableOptions.some(
                    (avail) => avail.value === option.value
                );
                const optionElement = createElement(
                    "option",
                    {
                        value: option.value,
                        style: isAvailable ? "" : "display: none",
                    },
                    [option.label]
                );
                select.appendChild(optionElement);
            });
            select.value = prevValue;
        });
    }
}

export const ordinaryLosses = new LossesTableSelectConfig(
    formSpec.claim_form.en.claim_transactions[0].fields[0].values,
    '[class*="loss-type-ordinary"]',
    "#add_transactions_1",
    "#remove_transactions_1",
    1500,
    'input[id^="amount-of-loss-ordinary__"]',
    "ordinary-losses-error"
);

export const extraordinaryLosses = new LossesTableSelectConfig(
    formSpec.claim_form.en.claim_transactions[1].fields[0].values,
    '[class*="loss-type-extraordinary"]',
    "#add_transactions_2",
    "#remove_transactions_2",
    10000,
    'input[id^="amount-of-loss-extraordinary__"]',
    "extraordinary-losses-error"
);

const components = {
    eligibleRadioGroup: {
        yes: () => document.getElementById("was-the-claimant-sent-a-notice_1"),
        no: () => document.getElementById("was-the-claimant-sent-a-notice_2"),
        notEligibleText: () =>
            document.querySelector(
                '[data-field-id="_not-eligible-to-submit-claim-paragraph"]'
            ),
    },
    attestTimeSpentHours: {
        yes: () =>
            document.getElementById(
                "does-the-claimant-attest-time-spent-remedying-issues_1"
            ),
        no: () =>
            document.getElementById(
                "does-the-claimant-attest-time-spent-remedying-issues_2"
            ),
    },
    californiaResident: {
        yes: () => document.getElementById("is-california-resident_1"),
        no: () => document.getElementById("is-california-resident_2"),
        warningContainerRadio: () =>
            document.getElementById("california-resident-warning-radio"),
    },
    contactInfo: {
        state: () => document.getElementById("state"),
        country: () => document.getElementById("country"),
    },
};

/**
 * Initializes custom functionality for the claim form.
 *
 * @param {string} claim - The claim being processed.
 */
export function customInit() {
    initializeGeneralInstructions();
    initializeCertificationAndSignature();

    setTablesStyles();
    iterateAndRenderSelects(ordinaryLosses);
    iterateAndRenderSelects(extraordinaryLosses);
    refreshSelectsOnNewRows(ordinaryLosses);
    refreshSelectsOnNewRows(extraordinaryLosses);
    setupLossesValidation(ordinaryLosses);
    setupLossesValidation(extraordinaryLosses);
    toggleAllFieldsGivenEligibility();
    toggleAttestTimeSpentHours();
    setupCaliforniaResidentValidation();
    fixAmountClassNames();
    addAnalyticsHooks();
}

function addAnalyticsHooks() {
    document.getElementById("submit-claim").addEventListener("click", () => {
        window.gtag_report_conversion();
        if (fbq) {
            fbq("track", "Lead");
        }
    });
}

function setTablesStyles() {
    const [ordinaryLossesTable, extraordinaryLossesTable] =
        document.querySelectorAll("table");

    ordinaryLossesTable.parentNode.insertBefore(
        createElement("div", {}, [
            createElement("h3", { class: "mb-3 text-decoration-underline" }, [
                "CLAIM FOR REIMBURSEMENT FOR A DOCUMENTED ORDINARY LOSS PAYMENT",
            ]),
            createElement("p", {}, [
                "Settlement Class Members who submit a valid and timely Claim Form are eligible to receive reimbursement of up to $1,500 in an Ordinary Documented Loss Payment that is reasonably traceable to the Incidents.  ",
            ]),
        ]),
        ordinaryLossesTable
    );
    extraordinaryLossesTable.parentNode.insertBefore(
        createElement("div", {}, [
            createElement(
                "h3",
                { class: "mb-3 mt-5 text-decoration-underline" },
                [
                    "CLAIM FOR REIMBURSEMENT FOR A DOCUMENTED EXTRAORDINARY LOSS PAYMENT",
                ]
            ),
            createElement("p", {}, [
                "Settlement Class Members are eligible for compensation for extraordinary losses resulting from the Incident, up to a maximum of $10,000.00, upon submission of a valid Claim Form and supporting documentation.",
            ]),
        ]),
        extraordinaryLossesTable
    );
    [ordinaryLossesTable, extraordinaryLossesTable].forEach((table) => {
        table.querySelector("caption").remove();
        // Check if colgroup already exists to avoid duplicates
        if (!table.querySelector("colgroup")) {
            // Create colgroup element
            const colgroup = createElement("colgroup", {}, [
                createElement("col", { style: "width: 300px;" }),
                createElement("col", { style: "width: 230px;" }),
                createElement("col", { style: "width: 250px;" }),
                createElement("col", { style: "width: auto;" }),
            ]);

            // Insert colgroup as the first child of the table
            table.insertBefore(colgroup, table.firstChild);
        }
    });

    // Insert error containers under each table if not present
    if (!document.getElementById(ordinaryLosses.errorContainerId)) {
        const errorContainer = createElement(
            "div",
            { id: ordinaryLosses.errorContainerId, class: "text-danger mt-2" },
            []
        );
        ordinaryLossesTable.parentNode.insertBefore(
            errorContainer,
            ordinaryLossesTable.nextSibling
        );
    }
    if (!document.getElementById(extraordinaryLosses.errorContainerId)) {
        const errorContainer = createElement(
            "div",
            {
                id: extraordinaryLosses.errorContainerId,
                class: "text-danger mt-2",
            },
            []
        );
        extraordinaryLossesTable.parentNode.insertBefore(
            errorContainer,
            extraordinaryLossesTable.nextSibling
        );
    }
}

function fixAmountClassNames() {
    const amountFields = document.querySelectorAll("[name*='amount-of-loss-']");
    amountFields.forEach((field) => field.classList.add("form-control"));
}

function toggleAllFieldsGivenEligibility() {
    const notEligibleToSubmitClaimParagraph =
        components.eligibleRadioGroup.notEligibleText();
    const notEligibleText = notEligibleToSubmitClaimParagraph.firstChild;
    const parent = notEligibleToSubmitClaimParagraph.parentElement;
    const subsequent = [...parent.children].slice(
        [...parent.children].indexOf(notEligibleToSubmitClaimParagraph) + 1
    );

    subsequent.forEach((child) => toggleVisibility(child, false));

    const eligibleRadioGroupNo = components.eligibleRadioGroup.no();
    const eligibleRadioGroupYes = components.eligibleRadioGroup.yes();
    eligibleRadioGroupNo.addEventListener("change", () => {
        notEligibleText.classList.remove("d-none");
        subsequent.forEach((child) => toggleVisibility(child, false));
    });
    eligibleRadioGroupYes.addEventListener("change", () => {
        notEligibleText.classList.add("d-none");
        subsequent.forEach((child) => toggleVisibility(child, true));
        toggleAttestTimeSpentHours();
    });
}

function toggleAttestTimeSpentHours() {
    const attestTimeSpentHoursYes = components.attestTimeSpentHours.yes();
    const attestTimeSpentHoursNo = components.attestTimeSpentHours.no();
    const hoursField = document.getElementById("how-many-hours");
    if (!hoursField || !attestTimeSpentHoursYes || !attestTimeSpentHoursNo)
        return;
    hoursField.parentElement.classList.add("d-none");
    attestTimeSpentHoursYes.addEventListener("change", () =>
        toggleVisibility(hoursField.parentElement, true)
    );
    attestTimeSpentHoursNo.addEventListener("change", () =>
        toggleVisibility(hoursField.parentElement, false)
    );
}

function setupCaliforniaResidentValidation() {
    const californiaResidentYes = components.californiaResident.yes();
    const californiaResidentNo = components.californiaResident.no();
    const contactInfoSection = document.getElementById("contact-information");
    const radioGroup = californiaResidentYes?.parentElement?.parentElement;

    if (
        !californiaResidentYes ||
        !californiaResidentNo ||
        !contactInfoSection ||
        !radioGroup
    )
        return;

    // Create warning container for radio group if it doesn't exist
    if (!components.californiaResident.warningContainerRadio()) {
        const warningContainerRadio = createElement(
            "div",
            {
                id: "california-resident-warning-radio",
                class: "alert alert-warning mt-3 d-none",
                role: "alert",
            },
            [
                createElement("strong", {}, ["Important: "]),
                "You must submit supporting documentation proving residency in California before August 2022.",
            ]
        );
        // Insert after radio group
        radioGroup.parentElement.insertBefore(
            warningContainerRadio,
            radioGroup.nextSibling
        );
    }

    californiaResidentYes.addEventListener("change", () => {
        if (requiresCaliforniaResidencyDocumentation()) {
            showCaliforniaResidentRadioWarning();
        }
    });

    californiaResidentNo.addEventListener("change", () => {
        hideCaliforniaResidentRadioWarning();
    });

    // Monitor state and country changes
    const stateField = components.contactInfo.state();
    const countryField = components.contactInfo.country();
    const handleContactChange = () => updateCaliforniaResidentContactWarning();

    if (stateField) {
        stateField.addEventListener("change", handleContactChange);
    }
    if (countryField) {
        countryField.addEventListener("change", handleContactChange);
    }
}

function showCaliforniaResidentRadioWarning() {
    const warningContainer =
        components.californiaResident.warningContainerRadio();
    if (warningContainer) {
        warningContainer.classList.remove("d-none");
    }
}

function hideCaliforniaResidentRadioWarning() {
    const warningContainer =
        components.californiaResident.warningContainerRadio();
    if (warningContainer) {
        warningContainer.classList.add("d-none");
    }
}

/**
 * Checks if the California resident radio button is selected as "Yes"
 * @returns {boolean} - True if user attested to being a California resident
 */
function isCaliforniaResidentSelected() {
    const californiaResidentYes = components.californiaResident.yes();
    return californiaResidentYes && californiaResidentYes.checked;
}

/**
 * Checks if the contact info address is not in California/USA
 * @returns {boolean} - True if address is outside California or USA
 */
function hasNonCaliforniaAddress() {
    const stateField = components.contactInfo.state();
    const countryField = components.contactInfo.country();

    return (
        stateField &&
        countryField &&
        stateField.value !== "" &&
        countryField.value !== "" &&
        (stateField.value !== "CA" || countryField.value !== "USA")
    );
}

/**
 * Checks if California residency documentation is required
 * @returns {boolean} - True if documentation is required
 */
function requiresCaliforniaResidencyDocumentation() {
    // Documentation is required if user attested California residency OR has non-California address
    return isCaliforniaResidentSelected() && hasNonCaliforniaAddress();
}

function updateCaliforniaResidentContactWarning() {
    if (requiresCaliforniaResidencyDocumentation()) {
        showCaliforniaResidentRadioWarning();
    } else {
        hideCaliforniaResidentRadioWarning();
    }
}

/**
 * Toggles visibility and handles required/data-validate attributes for input elements
 * @param {HTMLElement} element - The element to toggle visibility for
 * @param {boolean} isVisible - Whether the element should be visible
 */
function toggleVisibility(element, isVisible = true) {
    // Toggle the d-none class for visibility
    element.classList.toggle("d-none", !isVisible);

    // Handle required and data-validate attributes for inputs and their children
    toggleInputValidation(element, isVisible);
}

/**
 * Recursively handles required and data-validate attributes for input elements
 * @param {HTMLElement} element - The element to process
 * @param {boolean} isVisible - Whether the element should be visible
 */
function toggleInputValidation(element, isVisible) {
    // If element is an input, handle its attributes
    if (["INPUT", "SELECT", "TEXTAREA"].includes(element.tagName)) {
        toggleFieldRequired(element, isVisible);
        return;
    }

    // Query for children that are input, select, or textarea elements
    const inputChildren = element.querySelectorAll("input, select, textarea");
    inputChildren.forEach((child) => {
        toggleFieldRequired(child, isVisible);
    });
}

function toggleFieldRequired(element, isVisible) {
    if (isVisible) {
        // Add required and data-validate attributes when visible
        element.setAttribute("required", "");
        element.setAttribute("data-validate", "required");
    } else {
        // Remove required and data-validate attributes when hidden
        element.removeAttribute("required");
        element.removeAttribute("data-validate");
    }
}

function refreshSelectsOnNewRows(lossesTable) {
    const addButton = lossesTable.addButton();
    const removeButton = lossesTable.removeButton();

    addButton.addEventListener("click", () => {
        fixAmountClassNames();
        iterateAndRenderSelects(lossesTable);
        // Attach listeners for any new amount inputs if limit is configured
        if (lossesTable.limit !== null) {
            attachAmountListeners(lossesTable);
            validateLosses(lossesTable);
        }
    });
    removeButton.addEventListener("click", () => {
        iterateAndRenderSelects(lossesTable);
        const index = lossesTable.selectedValues.size - 1;
        if (index >= 0) {
            lossesTable.deleteValue(index);
        }
        if (lossesTable.limit !== null) {
            // Re-validate after a row removal
            validateLosses(lossesTable);
        }
    });
}

function iterateAndRenderSelects(lossesTable) {
    if (lossesTable.tds().length === 0) return;

    lossesTable.refreshSelectValues();
    lossesTable.tds().forEach((td, index) => {
        const select = td.querySelector("select");
        if (!select) return;
        if (select.parentElement.classList.contains("d-none")) return;

        const onChange = (e) => {
            const hasValue = Boolean(e.target.value);
            if (hasValue) {
                select.value = e.target.value;
                lossesTable.setValue(index, e.target.value);
                onSelectValueChange(select, td, lossesTable, index);
                return;
            }
            lossesTable.setValue(index, null);
        };
        select.removeEventListener("change", lossesTable.listeners.get(index));
        select.addEventListener("change", onChange);
        lossesTable.listeners.set(index, onChange);
    });
}

function onSelectValueChange(select, td, lossesTable, index) {
    const selectedOption = lossesTable.options.find(
        (o) => o.value === lossesTable.getValue(index)
    );
    if (!selectedOption) return;
    toggleVisibility(select.parentElement, false);
    const button = createElement(
        "button",
        { class: "btn btn-link text-primary", type: "button" },
        ["Change"]
    );
    let insertedDiv = null;
    button.addEventListener("click", () => {
        insertedDiv?.remove();
        toggleVisibility(select.parentElement, true);
        lossesTable.setValue(index, null);
    });
    insertedDiv = createElement("div", {}, [selectedOption.label, button]);

    td.insertBefore(insertedDiv, select.parentElement);
}

// Losses validation (reusable for ordinary and extraordinary) -----------------------------

function setupLossesValidation(lossesTable) {
    if (lossesTable.limit === null) return;
    // Attach listeners now and perform initial validation
    attachAmountListeners(lossesTable);
    validateLosses(lossesTable);
}

function attachAmountListeners(lossesTable) {
    const inputs = document.querySelectorAll(lossesTable.amountInputSelector);
    inputs.forEach((input) => {
        // Ensure numeric input behavior is consistent
        const handler = () => validateLosses(lossesTable);
        input.removeEventListener("input", handler);
        input.addEventListener("input", handler);
        input.removeEventListener("change", handler);
        input.addEventListener("change", handler);
    });
}

function getLossesTotal(lossesTable) {
    const inputs = document.querySelectorAll(lossesTable.amountInputSelector);
    let total = 0;
    inputs.forEach((input) => {
        const value = parseFloat(String(input.value).replace(/[^0-9.\-]/g, ""));
        if (!isNaN(value)) total += value;
    });
    return total;
}

function renderLossesError(lossesTable, message = "") {
    const container = document.getElementById(lossesTable.errorContainerId);
    if (!container) return;
    container.textContent = message;
}

function validateLosses(lossesTable) {
    if (lossesTable.limit === null) return true;
    const total = getLossesTotal(lossesTable);
    const inputs = document.querySelectorAll(lossesTable.amountInputSelector);

    if (total > lossesTable.limit) {
        renderLossesError(
            lossesTable,
            `Total cannot exceed $${lossesTable.limit.toLocaleString()}.`
        );
        // Add is-invalid class to all amount inputs
        inputs.forEach((input) => {
            input.classList.add("is-invalid");
        });
        return false;
    }

    renderLossesError(lossesTable, "");
    // Remove is-invalid class from all amount inputs
    inputs.forEach((input) => {
        input.classList.remove("is-invalid");
    });
    return true;
}

function initializeGeneralInstructions() {
    const settlementOverviewPlaceholder = document.getElementById(
        "settlement_overview_placeholder"
    );
    const settlementOverview = document.getElementById("settlement_overview");
    settlementOverviewPlaceholder.innerHTML = settlementOverview.innerHTML;
}

function initializeCertificationAndSignature() {
    const certificationAndSignaturePlaceholder = document.getElementById(
        "certification_and_signature_placeholder"
    );
    const certificationAndSignature = document.getElementById(
        "certification_and_signature"
    );
    certificationAndSignaturePlaceholder.innerHTML =
        certificationAndSignature.innerHTML;
}

/**
 * Custom process function for claim processing.
 *
 * @param {Object} claim - The claim object to be processed.
 * @returns {Object} - The processed claim object.
 */
export function customProcess(claim) {
    if (components.eligibleRadioGroup.no().checked) {
        claim.flags.valid = false;
        claim.flags.ineligible = true;
    } else {
        claim.flags.ineligible = false;
    }

    // Validate losses limits before submission
    if (!validateLosses(ordinaryLosses)) {
        claim.flags.valid = false;
    }
    if (!validateLosses(extraordinaryLosses)) {
        claim.flags.valid = false;
    }

    // Initialize documentation requirement flags
    claim.flags.hasOrdinaryLosses = false;
    claim.flags.hasExtraordinaryLosses = false;
    claim.flags.requiresCaliforniaResidencyDoc = false;

    // Check if user added ordinary losses (based on selected values)
    claim.flags.hasOrdinaryLosses = ordinaryLosses.selectedValues.size > 0;
    claim.flags.hasExtraordinaryLosses =
        extraordinaryLosses.selectedValues.size > 0;

    // Check if user must submit supporting documentation for California residency
    claim.flags.requiresCaliforniaResidencyDoc =
        requiresCaliforniaResidencyDocumentation();

    // If any documentation is required but no files have been uploaded, mark as invalid
    const hasDocumentationRequirements =
        claim.flags.hasOrdinaryLosses ||
        claim.flags.hasExtraordinaryLosses ||
        claim.flags.requiresCaliforniaResidencyDoc;

    if (hasDocumentationRequirements && claim.uploads.length === 0) {
        claim.flags.valid = false;
    }

    return claim;
}

export function customMember(claim) {
    return claim;
}
